import { ArrowLeft, Music, Heart, Sparkles, Headphones, Mic, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function MusicalExpressionGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-violet-700 hover:text-violet-800 hover:bg-violet-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-violet-100 rounded-full">
              <Music className="w-8 h-8 text-violet-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Musical Expression</h1>
              <p className="text-violet-600">Week 11, Day 7 - Creative Expression</p>
            </div>
          </div>

          <div className="prose prose-violet max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Today, engage with music actively. Whether you sing, play an instrument, or curate the 
              perfect playlist, let music move you and express what words cannot.
            </p>

            <div className="bg-violet-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-violet-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                The Power of Music
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Reduces stress and anxiety instantly</li>
                <li>• Expresses emotions that words can't capture</li>
                <li>• Connects you to joy, memories, and meaning</li>
                <li>• Improves mood within minutes</li>
                <li>• Provides emotional regulation</li>
                <li>• Creates community and shared experience</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Ways to Engage with Music</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Mic className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Sing!</h3>
                </div>
                <p className="text-gray-600 text-sm mb-2">Belt out your favorites in the shower, car, or living room. Singing releases tension and endorphins.</p>
                <p className="text-violet-600 text-sm italic">Tip: You don't need to be "good" - just let it out!</p>
              </div>
              
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Music className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Play an Instrument</h3>
                </div>
                <p className="text-gray-600 text-sm mb-2">Even if you haven't played in years, pick it up. Mess around. Have fun with it.</p>
                <p className="text-violet-600 text-sm italic">No instrument? Try a free piano or guitar app!</p>
              </div>
              
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Headphones className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Create a Playlist</h3>
                </div>
                <p className="text-gray-600 text-sm mb-2">Curate songs for a specific mood, memory, or intention. Music curation is creative expression.</p>
                <p className="text-violet-600 text-sm italic">Ideas: "Songs that make me feel powerful" or "My 20s soundtrack"</p>
              </div>
              
              <div className="bg-white border-2 border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Radio className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-gray-900">Discover New Music</h3>
                </div>
                <p className="text-gray-600 text-sm mb-2">Explore a genre you've never tried. Let Spotify's "Discover Weekly" surprise you.</p>
                <p className="text-violet-600 text-sm italic">New music creates new neural pathways!</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-violet-800 mb-4">Playlist Ideas</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">By Mood</h3>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• "Songs for a bad day"</li>
                    <li>• "Energy boost"</li>
                    <li>• "Sunday morning peace"</li>
                    <li>• "Confidence anthem"</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">By Memory</h3>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• "Songs from high school"</li>
                    <li>• "My parents played this"</li>
                    <li>• "Summer road trips"</li>
                    <li>• "First love songs"</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Today's Challenge
              </h2>
              <p className="text-gray-700 mb-4">Choose at least one:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Sing at least 3 songs at full volume</li>
                <li>• Pick up an instrument for 15 minutes (skill doesn't matter)</li>
                <li>• Create a 10-song playlist with a specific theme</li>
                <li>• Discover and really listen to 3 new artists</li>
                <li>• Listen to an album start to finish (no skipping)</li>
              </ul>
            </div>

            <div className="bg-rose-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-rose-800 mb-4">
                Music for Different Moments
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Need energy?</strong> Upbeat pop, dance music, rock anthems</li>
                <li>• <strong>Need calm?</strong> Acoustic, classical, lo-fi beats, nature sounds</li>
                <li>• <strong>Need motivation?</strong> Empowerment anthems, movie soundtracks</li>
                <li>• <strong>Need to feel?</strong> Emotional ballads, meaningful lyrics</li>
                <li>• <strong>Need joy?</strong> Nostalgic favorites, feel-good hits</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
